 import React, { useEffect, useState, useMemo } from "react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { Refresh, Block, Search, CheckCircle } from "@mui/icons-material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Tooltip, InputAdornment, TextField } from '@mui/material';
import {
  UserRound,
} from "lucide-react";
import "../../css/HRPortal.css";
import Pagination from './Pagination';
import ReusableSearchBar from "./components/ReusableSearchBar/ReusableSearchBar.jsx";
import ReusableTable from "./components/ReusableTable/ReusableTable.jsx";
import requestDemoService, { extractCompanyNameFromEmail } from "./services/requestDemoService"; 
import { useSuspendReactivateAdmin } from "./hooks/useSuspendReactivateAdmin";

const RequestDemoContent = () => {
  const [acceptedDemos, setAcceptedDemos] = useState([]);
  const [loading, setLoading] = useState(true);
    // Pagination states for trial expirations
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
    // Pagination states for expiring admins
  const [currentAdminPage, setCurrentAdminPage] = useState(1);
  const [adminItemsPerPage] = useState(10);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [trialSearchTerm, setTrialSearchTerm] = useState("");
  const [isCreatingAdmin, setIsCreatingAdmin] = useState({});
  const [emailSentDemos, setEmailSentDemos] = useState(new Set());

  const [expiringAdmins, setExpiringAdmins] = useState([]);

  const {
    showSuspendModal,
    openSuspendModal,
    closeSuspendModal,
    reason,
    setReason,
    isSuspending,
    suspendAdmin,
    reactivateAdmin,
    isReactivating,
  } = useSuspendReactivateAdmin(() => loadData()); // This refreshes both tables

  // At the top with your other useState
  const [demoVisibleColumns] = useState({
    company: true,
    name: true,
    workEmail: true,
    phoneNumber: true,
    created_at: true,
    action: true,
  });

  const [adminVisibleColumns] = useState({
    org_admin_name: true,
    org_admin_email: true,
    end_date: true,
    daysRemaining: true,
    action: true,
  });

  // For Demos
  const [demoSortConfig, setDemoSortConfig] = useState({ key: "created_at", direction: "desc" });

  // For Admins
  const [adminSortConfig, setAdminSortConfig] = useState({ key: "end_date", direction: "asc" });

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null); // Reset error on retry
    try {
      const [demos, admins] = await Promise.all([
        requestDemoService.getAcceptedDemos(),
        requestDemoService.getExpiringOrgAdmins(),
      ]);
      setAcceptedDemos(demos);
      setExpiringAdmins(admins);
    } catch (error) {
      console.error("Failed to load initial dashboard data:", error);
      setError(error.message || "Failed to load data. Please try again.");
      toast.error("Some data failed to load. Please refresh.", {
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "NA";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "NA";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const createAdminFromDemo = async (demo) => {
    setIsCreatingAdmin((prev) => ({ ...prev, [demo._id]: true }));
    try {
      await requestDemoService.createAdminFromDemo(demo);
      setEmailSentDemos((prev) => new Set(prev).add(demo._id));
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Admin account created successfully!",
      });
      await loadData(); // Refresh data to reflect changes
    } catch (error) {
      console.error("Error creating admin:", error);
      const errorMessage = error.message || "Unknown error";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to create admin account: ${errorMessage}`,
      });
    } finally {
      setIsCreatingAdmin((prev) => ({ ...prev, [demo._id]: false }));
    }
  };

  // Calculate days remaining until end date
  const getDaysRemaining = (endDateString) => {
    if (!endDateString) return "N/A";
    const endDate = new Date(endDateString);
    const today = new Date();
    const timeDiff = endDate - today;
    const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysRemaining;
  };

  // Get color class based on days remaining
  const getDaysRemainingColor = (days) => {
    if (days <= 3) return "text-red-600 font-bold";
    if (days <= 7) return "text-orange-600 font-semibold";
    return "text-yellow-600";
  };

  const demoColumns = [
    {
      key: "company",
      label: "Company",
      sortable: true,
      width: "200px",
      render: (_, row) => extractCompanyNameFromEmail(row.workEmail),
    },
    {
      key: "name",
      label: "Name",
      sortable: true,
      width: "180px",
    },
    {
      key: "workEmail",
      label: "Email",
      sortable: true,
      width: "220px",
      render: (email) => (
        <Tooltip title={email} placement="top">
          <span
            className="cursor-pointer hover:text-blue-300"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(email);
              toast.success("Email copied!", { autoClose: 1500 });
            }}
          >
            {truncateText(email, 25)}
          </span>
        </Tooltip>
      ),
    },
    {
      key: "phoneNumber",
      label: "Phone",
      sortable: false,
      width: "140px",
      render: (phone) => phone || "NA",
    },
    {
      key: "created_at",
      label: "Accepted Date",
      sortable: true,
      width: "140px",
      render: (date) => formatDate(date),
    },
    {
      key: "action",
      label: "Action",
      sortable: false,
      width: "160px",
      render: (_, row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            createAdminFromDemo(row);
          }}
          disabled={emailSentDemos.has(row._id) || isCreatingAdmin[row._id]}
          className={`px-4 py-2 rounded text-white text-xs transition-colors flex items-center gap-2 ${
            emailSentDemos.has(row._id) || isCreatingAdmin[row._id]
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
          }`}
        >
          <UserRound className="w-4 h-4" />
          {isCreatingAdmin[row._id] ? "Creating..." : "Create Admin"}
        </button>
      ),
    },
  ];
  
  const adminColumns = [
    {
      key: "org_admin_name",
      label: "Name",
      sortable: true,
      width: "200px",
    },
    {
      key: "org_admin_email",
      label: "Email",
      sortable: true,
      width: "240px",
      render: (email) => (
        <Tooltip title={email || "NA"} placement="top">
          <span>{truncateText(email, 28) || "NA"}</span>
        </Tooltip>
      ),
    },
    {
      key: "end_date",
      label: "End Date",
      sortable: true,
      width: "140px",
      render: (date) => formatDate(date),
    },
    {
      key: "daysRemaining",
      label: "Days Left",
      sortable: true,
      width: "120px",
      render: (_, row) => {
        const days = getDaysRemaining(row.end_date);
        const color = getDaysRemainingColor(days);
        return <span className={color}>{days} days</span>;
      },
    },
    {
      key: "action",
      label: "Action",
      sortable: false,
      width: "160px",
      render: (_, row) => (
        row.is_suspended === "true" ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              reactivateAdmin(row.org_admin_id);
            }}
            disabled={isReactivating}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition disabled:opacity-50"
          >
            <CheckCircle className="w-4 h-4" />
            {isReactivating ? "Reactivating..." : "Reactivate"}
          </button>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              openSuspendModal(row.org_admin_id);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition"
          >
            <Block className="w-4 h-4" />
            Suspend
          </button>
        )
      ),
    },
  ];

  // === DEMO TABLE: FILTER + SORT + PAGINATION ===
  const filteredAndSortedDemos = useMemo(() => {
    return acceptedDemos
      .filter(
        (demo) =>
          demo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          demo.workEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
          extractCompanyNameFromEmail(demo.workEmail).toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        const { key, direction } = demoSortConfig;
        if (key === "created_at") {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return direction === "asc" ? dateA - dateB : dateB - dateA;
        }
        if (key === "company") {
          const nameA = extractCompanyNameFromEmail(a.workEmail).toLowerCase();
          const nameB = extractCompanyNameFromEmail(b.workEmail).toLowerCase();
          return direction === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        }
        if (key === "name") {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
          return direction === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        }
        if (key === "workEmail") {
          const emailA = a.workEmail.toLowerCase();
          const emailB = b.workEmail.toLowerCase();
          return direction === "asc" ? emailA.localeCompare(emailB) : emailB.localeCompare(emailA);
        }
        return 0;
      });
  }, [acceptedDemos, searchTerm, demoSortConfig]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAndSortedDemos.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredAndSortedDemos.length / itemsPerPage);

  const filteredExpiringAdmins = useMemo(() => {
    const term = trialSearchTerm.toLowerCase();
    return expiringAdmins
      .filter((admin) => {
        return (
          admin.org_admin_name?.toLowerCase().includes(term) ||
          admin.org_admin_email?.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        const { key, direction } = adminSortConfig;
        if (key === "end_date" || key === "daysRemaining") { // daysRemaining sorts by end_date
          const dateA = new Date(a.end_date);
          const dateB = new Date(b.end_date);
          return direction === "asc" ? dateA - dateB : dateB - dateA;
        }
        if (key === "org_admin_name") {
          const nameA = a.org_admin_name?.toLowerCase() || "";
          const nameB = b.org_admin_name?.toLowerCase() || "";
          return direction === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        }
        if (key === "org_admin_email") {
          const emailA = a.org_admin_email?.toLowerCase() || "";
          const emailB = b.org_admin_email?.toLowerCase() || "";
          return direction === "asc" ? emailA.localeCompare(emailB) : emailB.localeCompare(emailA);
        }
        return 0;
      });
  }, [expiringAdmins, trialSearchTerm, adminSortConfig]);

  const indexOfLastAdminItem = currentAdminPage * adminItemsPerPage;
  const indexOfFirstAdminItem = indexOfLastAdminItem - adminItemsPerPage;
  const currentAdminItems = filteredExpiringAdmins.slice(
    indexOfFirstAdminItem,
    indexOfLastAdminItem
  );
  const totalAdminPages = Math.ceil(
    filteredExpiringAdmins.length / adminItemsPerPage
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 w-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-500">
        <p>{error}</p>
        <button
          onClick={loadData}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
  <>
    {/* ====================== STEP 1: BLUR EVERYTHING WHEN MODAL IS OPEN ====================== */}
    <div
      className={`transition-all duration-300 ${
        showSuspendModal ? "blur-lg pointer-events-none" : ""
      }`}
    >
      <div className="glass-effect">
        {/* === ACCEPTED DEMO REQUESTS SECTION === */}
        {acceptedDemos.length > 0 && (
          <div className="p-6 mb-6 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-light text-white">
                Accepted Demo Requests
              </h2>
              <div className="flex items-center gap-3">
                <ReusableSearchBar
                  value={searchTerm}
                  onChange={(value) => {
                    setSearchTerm(value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search name, email, company..."
                  width="340px"
                  minWidth="280px"
                  maxWidth="400px"
                  showClear={true}
                />
              </div>
            </div>

            <ReusableTable
              columns={demoColumns}
              data={currentItems}
              visibleColumns={demoVisibleColumns}
              sortConfig={demoSortConfig}
              onSort={setDemoSortConfig}
              noDataMessage="No accepted demo requests"
            />

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredAndSortedDemos.length}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        )}
      </div>

      <div className="glass-effect">
        {/* === ACCOUNT EXPIRY NOTIFICATIONS SECTION === */}
        {expiringAdmins.length > 0 && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-light text-white">
                Account Expiry Notifications
              </h2>
              <div className="flex items-center gap-3">
                <ReusableSearchBar
                  value={trialSearchTerm}
                  onChange={(value) => {
                    setTrialSearchTerm(value);
                    setCurrentAdminPage(1);
                  }}
                  placeholder="Search admin name or email..."
                  width="340px"
                  minWidth="280px"
                  maxWidth="380px"
                  showClear={true}
                />
              </div>
            </div>

            <ReusableTable
              columns={adminColumns}
              data={currentAdminItems}
              visibleColumns={adminVisibleColumns}
              sortConfig={adminSortConfig}
              onSort={setAdminSortConfig}
              noDataMessage="No accounts expiring soon"
              truncateLength={30}
            />

            {totalAdminPages > 1 && (
              <Pagination
                currentPage={currentAdminPage}
                totalPages={totalAdminPages}
                totalItems={filteredExpiringAdmins.length}
                onPageChange={setCurrentAdminPage}
              />
            )}
          </div>
        )}

        {acceptedDemos.length === 0 && expiringAdmins.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No demo requests found</p>
          </div>
        )}
      </div>
    </div>

    {/* ====================== STEP 2: SUSPEND MODAL (UNCHANGED, JUST MOVED OUTSIDE BLUR) ====================== */}
    {showSuspendModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Dark overlay */}
        <div
          className="absolute inset-0 bg-black bg-opacity-60"
          onClick={closeSuspendModal}
        />

        {/* Modal card */}
        <div
          className="relative z-10 w-full max-w-xl p-6 rounded-lg shadow-2xl"
          style={{
            background:
              "linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))",
            backdropFilter: "blur(40px)",
            WebkitBackdropFilter: "blur(50px)",
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-[#C1A0D0] text-lg">Suspend Organization Admin</h3>
            <button onClick={closeSuspendModal}>
              <CloseRoundedIcon className="h-6 w-6 text-[#C1A0D0] hover:text-white transition" />
            </button>
          </div>

          <textarea
            className="w-full px-4 py-3 bg-transparent border border-gray-600/50 rounded-lg text-[#CCCCCC] placeholder-gray-500 focus:outline-none focus:border-pink-500/50 resize-none"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for suspension (required)"
            rows={5}
          />

          <div className="flex justify-end gap-4 mt-6">
            <button
              onClick={closeSuspendModal}
              className="px-8 py-2.5 border border-gray-600 text-[#CCCCCC] rounded-full hover:bg-gray-800/50 transition text-sm"
            >
              Cancel
            </button>
            <button
              onClick={suspendAdmin}
              disabled={isSuspending || !reason.trim()}
              className="px-8 py-2.5 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full hover:from-pink-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2 text-sm"
            >
              {isSuspending && (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              )}
              {isSuspending ? "Suspending..." : "Suspend Admin"}
            </button>
          </div>
        </div>
      </div>
    )}
  </>
);
};

export default RequestDemoContent;